"use client";

import { useEffect, useRef } from "react";
import { X, ShoppingBag } from "lucide-react";
import { CartLineRow } from "@/components/cart/CartLineRow";
import { CartSummary } from "@/components/cart/CartSummary";
import { EmptyCartState } from "@/components/cart/EmptyCartState";
import { useCart } from "@/store/cart";

export function CartDrawer() {
  const { cart, isOpen, isLoading, pendingLineId, error, closeCart, removeItem, updateItemQuantity } =
    useCart();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeCart();
    };
    if (isOpen) document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isOpen, closeCart]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  const lines = cart?.lines.nodes ?? [];
  const subtotal = cart?.cost.subtotalAmount;

  return (
    <>
      {/* Backdrop */}
      <div
        ref={overlayRef}
        onClick={closeCart}
        className="fixed inset-0 z-50 bg-black/60 transition-opacity"
        style={{
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? "auto" : "none",
        }}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        role="dialog"
        aria-modal={isOpen}
        aria-hidden={!isOpen}
        aria-label="Shopping cart"
        inert={!isOpen}
        className="fixed inset-y-0 right-0 z-50 w-full max-w-md flex flex-col shadow-2xl transition-transform duration-300"
        style={{
          backgroundColor: "#141414",
          borderLeft: "1px solid #2A2A2A",
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          pointerEvents: isOpen ? "auto" : "none",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: "#2A2A2A" }}
        >
          <div className="flex items-center gap-2">
            <ShoppingBag size={20} style={{ color: "#E8C547" }} />
            <h2 className="text-base font-semibold" style={{ fontFamily: "var(--font-display)" }}>
              Your Cart
            </h2>
            {cart && cart.totalQuantity > 0 && (
              <span
                className="text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: "#E8C547", color: "#0A0A0A" }}
              >
                {cart.totalQuantity}
              </span>
            )}
          </div>
          <button
            onClick={closeCart}
            className="p-1.5 rounded-md transition-colors hover:bg-[#1E1E1E]"
            style={{ color: "#9A9A9A" }}
            aria-label="Close cart"
          >
            <X size={20} />
          </button>
        </div>

        {/* Lines */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {isLoading && lines.length === 0 && (
            <div className="flex items-center justify-center py-16">
              <div
                className="w-6 h-6 border-2 rounded-full animate-spin"
                style={{ borderColor: "#3A3A3A", borderTopColor: "#E8C547" }}
              />
            </div>
          )}

          {error && (
            <div
              className="rounded-sm border px-3 py-2 text-xs"
              style={{ borderColor: "#E8404040", color: "#FCA5A5", backgroundColor: "#E8404010" }}
            >
              {error}
            </div>
          )}

          {!isLoading && lines.length === 0 && <EmptyCartState onNavigate={closeCart} />}

          {lines.map((line) => (
            <CartLineRow
              key={line.id}
              line={line}
              disabled={pendingLineId === line.id}
              onRemove={removeItem}
              onUpdateQuantity={updateItemQuantity}
            />
          ))}
        </div>

        {/* Footer */}
        {lines.length > 0 && (
          <div
            className="px-6 py-5 border-t space-y-3"
            style={{ borderColor: "#2A2A2A" }}
          >
            <CartSummary
              subtotal={subtotal}
              checkoutUrl={cart?.checkoutUrl}
              isLoading={isLoading || pendingLineId !== null}
              showViewCartLink
              onNavigate={closeCart}
            />
            <button
              onClick={closeCart}
              className="w-full py-2.5 text-sm font-medium transition-colors hover:text-[#F5F5F5]"
              style={{ color: "#9A9A9A" }}
            >
              Continue Shopping
            </button>
          </div>
        )}
      </div>
    </>
  );
}
